/**
 * Stage 4.1 — durable text outbox (IndexedDB).
 * Files/edit out of scope. Happy-path send still uses SDK local echo;
 * outbox covers offline / failed send + flush after sync revive.
 */
import type { IMessage } from '@/modules/chat-matrix-fast/types/IChat'
import { CHRONOLOGICAL_PENDING_ERROR } from './matrixPendingMessages'

export type OutboxStatus = 'pending' | 'sending' | 'failed'

export interface OutboxPendingItem {
  id: string
  chatId: number
  roomId: string
  topicId?: number | null
  body: string
  mentionUserIds?: string[]
  replyToId?: number | string
  replySnapshot?: {
    id: number | string
    matrix_event_id?: string
    content?: string
    attachments?: IMessage['attachments']
    senderName?: string
    created_by?: IMessage['created_by']
  }
  createdAt: string
  /** The Matrix transaction used by the first send and every retry. */
  clientTxnId: string
  status: OutboxStatus
  attempts: number
  lastError?: string
  /** Matrix MXID — не flush’ить чужой outbox после смены пользователя */
  userId: string
}

export const OUTBOX_MAX_ATTEMPTS = 5
export const OUTBOX_DB_NAME = 'daomed-chat-matrix-fast-outbox'
const LEGACY_OUTBOX_DB_NAME = 'daomed-chat-matrix-outbox'
export const OUTBOX_STORE = 'pending'
const OUTBOX_DB_VERSION = 1

const RECOVERABLE_PRE_SEND_ERRORS = new Set([
  CHRONOLOGICAL_PENDING_ERROR,
  'Promise.withResolvers is not a function',
])

/** These fixed SDK failures happened before HTTP; allow a fresh retry budget. */
export function recoverPreSendOutboxFailure(item: OutboxPendingItem): OutboxPendingItem {
  if (item.status !== 'failed' || !RECOVERABLE_PRE_SEND_ERRORS.has(item.lastError ?? '')) {
    return item
  }
  return { ...item, status: 'pending', attempts: 0, lastError: undefined }
}

export function outboxEventId(id: string): string {
  return `~outbox:${id}`
}

export function isOutboxEventId(eventId?: string | null): boolean {
  return Boolean(eventId?.startsWith('~outbox:'))
}

export function createOutboxId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `ob-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
}

export function hashOutboxKey(value: string): number {
  let hash = 0
  for (let i = 0; i < value.length; i += 1) {
    hash = ((hash << 5) - hash) + value.charCodeAt(i)
    hash |= 0
  }
  return Math.abs(hash) || 1
}

export function outboxItemToMessage(
  item: OutboxPendingItem,
  author?: { id?: string | number; full_name?: string; avatar?: string },
): IMessage {
  const matrixEventId = outboxEventId(item.id)
  return {
    id: hashOutboxKey(matrixEventId),
    matrix_event_id: matrixEventId,
    matrix_txn_id: item.clientTxnId,
    content: item.body,
    created_at: item.createdAt,
    author_id: author?.id ?? item.userId,
    author: String(author?.id ?? item.userId),
    created_by: author
      ? {
          id: author.id ?? item.userId,
          full_name: author.full_name,
          avatar: author.avatar,
        }
      : { id: item.userId },
    avatar: author?.avatar,
    is_delivered: false,
    reply_to_id: item.replyToId,
    reply_message_id: item.replyToId,
    reply_to: item.replySnapshot
      ? {
          id: item.replySnapshot.id,
          matrix_event_id: item.replySnapshot.matrix_event_id,
          content: item.replySnapshot.content,
          attachments: item.replySnapshot.attachments,
          created_by: item.replySnapshot.created_by,
        }
      : undefined,
    attachments: [],
    reactions: [],
  } as IMessage
}

function openOutboxDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB unavailable'))
      return
    }
    const request = indexedDB.open(OUTBOX_DB_NAME, OUTBOX_DB_VERSION)
    request.onerror = () => reject(request.error ?? new Error('IndexedDB open failed'))
    request.onsuccess = () => resolve(request.result)
    request.onupgradeneeded = () => {
      const db = request.result
      if (!db.objectStoreNames.contains(OUTBOX_STORE)) {
        const store = db.createObjectStore(OUTBOX_STORE, { keyPath: 'id' })
        store.createIndex('userId', 'userId', { unique: false })
        store.createIndex('chatId', 'chatId', { unique: false })
      }
    }
  })
}

/** Read the former client's pending queue without creating an empty legacy database. */
function openLegacyOutboxDb(): Promise<IDBDatabase | null> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      resolve(null)
      return
    }
    let missing = false
    const request = indexedDB.open(LEGACY_OUTBOX_DB_NAME)
    request.onupgradeneeded = () => {
      missing = true
      request.transaction?.abort()
    }
    request.onerror = () => missing ? resolve(null) : reject(request.error)
    request.onsuccess = () => {
      const db = request.result
      if (!db.objectStoreNames.contains(OUTBOX_STORE)) {
        db.close()
        resolve(null)
        return
      }
      resolve(db)
    }
  })
}

export async function putOutboxItem(item: OutboxPendingItem): Promise<void> {
  const db = await openOutboxDb()
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(OUTBOX_STORE, 'readwrite')
    tx.objectStore(OUTBOX_STORE).put(item)
    tx.oncomplete = () => {
      db.close()
      resolve()
    }
    tx.onerror = () => {
      db.close()
      reject(tx.error)
    }
  })
}

async function deleteDatabaseItem(db: IDBDatabase, id: string): Promise<void> {
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(OUTBOX_STORE, 'readwrite')
    tx.objectStore(OUTBOX_STORE).delete(id)
    tx.oncomplete = () => resolve()
    tx.onabort = tx.onerror = () => reject(tx.error)
  }).finally(() => db.close())
}

export async function deleteOutboxItem(id: string): Promise<void> {
  // A retry keeps the original transaction ID. Remove the old copy first so it
  // cannot reappear after a successful send or an explicit discard in Fast.
  const legacy = await openLegacyOutboxDb()
  if (legacy) {
    await deleteDatabaseItem(legacy, id)
  }
  await deleteDatabaseItem(await openOutboxDb(), id)
}

export async function clearOutboxDatabase(): Promise<void> {
  if (typeof indexedDB === 'undefined') {
    return
  }
  await Promise.all([OUTBOX_DB_NAME, LEGACY_OUTBOX_DB_NAME].map(name =>
    new Promise<void>(resolve => {
      const request = indexedDB.deleteDatabase(name)
      request.onsuccess = () => resolve()
      request.onerror = () => resolve()
      request.onblocked = () => resolve()
    }),
  ))
}

async function readDatabaseItems(db: IDBDatabase | null, userId: string): Promise<OutboxPendingItem[]> {
  if (!db) {
    return []
  }
  return new Promise<OutboxPendingItem[]>((resolve, reject) => {
    const tx = db.transaction(OUTBOX_STORE, 'readonly')
    const request = tx.objectStore(OUTBOX_STORE).index('userId').getAll(userId)
    request.onsuccess = () => resolve(request.result || [])
    request.onerror = () => reject(request.error)
    tx.oncomplete = () => db.close()
    tx.onabort = tx.onerror = () => {
      db.close()
      reject(tx.error)
    }
  })
}

export async function listOutboxItems(userId: string): Promise<OutboxPendingItem[]> {
  const [legacy, current] = await Promise.all([
    openLegacyOutboxDb().then(db => readDatabaseItems(db, userId)).catch(() => []),
    openOutboxDb().then(db => readDatabaseItems(db, userId)).catch(() => []),
  ])
  // Fast owns newer retry status; the old database remains a durable fallback
  // until the item is sent or discarded. Other users' queues are never read.
  return [...new Map([...legacy, ...current].map(item => [item.id, item])).values()]
    .map(recoverPreSendOutboxFailure)
    .sort((a, b) => Date.parse(a.createdAt) - Date.parse(b.createdAt))
}

export async function listOutboxForChat(
  userId: string,
  chatId: number | string,
  roomId?: string | null,
): Promise<OutboxPendingItem[]> {
  const all = await listOutboxItems(userId)
  return all.filter(item => {
    if (String(item.chatId) !== String(chatId)) {
      return false
    }
    if (roomId && item.roomId !== roomId) {
      return false
    }
    return item.status !== 'failed' || item.attempts < OUTBOX_MAX_ATTEMPTS
  })
}

export function buildOutboxItem(input: {
  chatId: number
  roomId: string
  topicId?: number | null
  body: string
  userId: string
  clientTxnId?: string
  mentionUserIds?: string[]
  replyToId?: number | string
  replySnapshot?: OutboxPendingItem['replySnapshot']
}): OutboxPendingItem {
  const id = createOutboxId()
  return {
    id,
    chatId: input.chatId,
    roomId: input.roomId,
    topicId: input.topicId ?? null,
    body: input.body,
    mentionUserIds: input.mentionUserIds,
    replyToId: input.replyToId,
    replySnapshot: input.replySnapshot,
    createdAt: new Date().toISOString(),
    clientTxnId: input.clientTxnId ?? id,
    status: 'pending',
    attempts: 0,
    userId: input.userId,
  }
}
